import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import PageHeader from "components/common/PageHeader";
import { SmsCampaignStatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getSmsHistoryList,
  resetFilters,
  setFilter,
} from "store/slices/smsHistorySlice";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate } from "utils/utils";
import { usePageParam } from "hooks/usePageParam";

const SmsCampaignHistory = () => {
  const { t } = useTranslation();
  const { view } = Icons || {};
  const dispatch = useDispatch();
  const [pageParam, setPageParam] = usePageParam();
  const { canAction } = usePermission();
  const { canView } = canAction("send_sms");

  const {
    loading,
    totalPages,
    items: smsHistory,
    currentPage,
    totalCount,
    filters: { perPage, pageNo, keyword, orderBy },
  } = useSelector((state) => state.SmsHistory);

  useEffect(() => {
    if (canView) {
      getAllData(pageParam);
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    setPageParam(page || 1);

    const payload = {
      perPage,
      pageNo: page || pageNo,
      orderBy,
      keyword: isReset || isResetKeyword ? "" : keyword || "",
    };

    dispatch(getSmsHistoryList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const columns = useMemo(
    () => [
      {
        header: t("Message"),
        key: "message",
        render: (value) => (
          <div className="text-truncate" style={{ maxWidth: 280 }} title={value}>
            {value || "-"}
          </div>
        ),
      },
      {
        header: t("Send To"),
        key: "type",
        render: (value) =>
          value === "specific_customers"
            ? t("Specific Customers")
            : t("All Customers"),
      },
      {
        header: t("Recipients"),
        key: "total",
        render: (value, item) => (
          <div className="g_start gap-1">
            <span>{item?.sent ?? 0}</span>
            <span className="text-muted">/</span>
            <span>{value ?? 0}</span>
            <span className="text-muted">{t("sent")}</span>
            {item?.failed > 0 && (
              <span className="text-danger ms-1">
                {item.failed} {t("failed")}
              </span>
            )}
          </div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <SmsCampaignStatusBadge status={value} t={t} />,
      },
      {
        header: t("Sent By"),
        key: "createdBy",
        render: (value) => value?.name || "-",
      },
      {
        header: t("Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text" style={{ whiteSpace: "nowrap" }}>
            {formatUTCDate(value)}
          </div>
        ),
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <Link to={`/send-sms/history/details?id=${item._id}`}>
        <img src={view} width={20} height={20} alt="view" />
      </Link>
    ),
    [],
  );

  if (!canView) return null;

  return (
    <div className="page-content">
      <PageHeader title={t("SMS Campaign History")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Message")}
        isDateRange={false}
        onSearch={getAllData}
        onReset={handleReset}
      />

      {loading && <TableShimmer rows={12} columns={5} />}

      {!loading && smsHistory?.length === 0 && (
        <NoDataFound title={t("No SMS History Found")} />
      )}
      {!loading && smsHistory?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={smsHistory}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default SmsCampaignHistory;
